import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import { captureError } from '@/lib/logger';

import {
    parseSignContractResult,
    SIGN_CONTRACT_UNKNOWN_ERROR,
    type SignContractResult,
} from '../signContract';

export interface Activation {
    /** The confirm surface — the proof-read, not an "are you sure?". */
    confirmOpen: boolean;
    openConfirm: () => void;
    closeConfirm: () => void;
    submit: () => void;
    pending: boolean;
    /** The server's sentences, verbatim. They land on the manifest rows they belong to. */
    refusals: string[];
    /** When the RPC said yes, this session. Null on a page that did not do the activating. */
    activatedAt: string | null;
    receiptOpen: boolean;
    openReceipt: () => void;
    closeReceipt: () => void;
}

interface Options {
    contractId: number;
    /** Re-read the page's own queries after the contract leaves DRAFT. */
    onActivated?: () => void;
}

/**
 * The one call that activates a contract, and everything it is allowed to say afterwards.
 *
 * `sign_contract` IS UNTOUCHED, and so are the three invalidations the screen this replaces
 * made — `contracts`, `contract-status-for-signing`, `contract-insights` — because a contract
 * that has just gone live changes what every one of those answers. What changed is where the
 * server's words go: they used to be joined with commas into a toast that read "Cannot start
 * contract: …" and vanished after four seconds. Now the refusal sentences are handed back
 * verbatim and `computeSetupState` lands each one ON THE ROW THAT CAN FIX IT, and success
 * returns a receipt instead of a toast. Nothing here paraphrases the server.
 *
 * A THROW IS NOT A REFUSAL. `sign_contract` returns its refusals as data, so a thrown error
 * means the call itself did not complete — logged through `captureError`, and shown as its own
 * sentence with no row attached rather than blamed on a gate.
 */
export function useActivation({ contractId, onActivated }: Options): Activation {
    const queryClient = useQueryClient();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [refusals, setRefusals] = useState<string[]>([]);
    const [activatedAt, setActivatedAt] = useState<string | null>(null);
    const [receiptOpen, setReceiptOpen] = useState(false);

    const mutation = useMutation({
        mutationFn: async (): Promise<SignContractResult> => {
            const { data, error } = await supabase.rpc('sign_contract', {
                contract_id_param: contractId,
            });
            if (error) throw error;
            return parseSignContractResult(data);
        },
        onSuccess: (result) => {
            setConfirmOpen(false);
            if (!result.ok) {
                setRefusals(
                    result.reasons.length > 0
                        ? result.reasons
                        : [result.message ?? SIGN_CONTRACT_UNKNOWN_ERROR],
                );
                // A REFUSAL MEANS THE TWO PICTURES CAME APART. The screen offered a call the
                // server declined, so the readiness payload is re-read rather than trusted: the
                // next paint is drawn from the answer that just refused.
                queryClient.invalidateQueries({ queryKey: ['contract-setup-status', contractId] });
                return;
            }
            setRefusals([]);
            setActivatedAt(new Date().toISOString());
            setReceiptOpen(true);
            queryClient.invalidateQueries({ queryKey: ['contracts'] });
            queryClient.invalidateQueries({ queryKey: ['contract-status-for-signing', contractId] });
            queryClient.invalidateQueries({ queryKey: ['contract-insights', String(contractId)] });
            // The contract has left DRAFT, which is `CONTRACT_NOT_DRAFT` on the very next read.
            queryClient.invalidateQueries({ queryKey: ['contract-setup-status', contractId] });
            onActivated?.();
        },
        onError: (error) => {
            captureError(error);
            setConfirmOpen(false);
            setRefusals([parseSignContractResult(error).message ?? SIGN_CONTRACT_UNKNOWN_ERROR]);
        },
    });

    const { mutate } = mutation;

    return {
        confirmOpen,
        openConfirm: useCallback(() => setConfirmOpen(true), []),
        closeConfirm: useCallback(() => setConfirmOpen(false), []),
        submit: useCallback(() => mutate(), [mutate]),
        pending: mutation.isPending,
        refusals,
        activatedAt,
        receiptOpen,
        openReceipt: useCallback(() => setReceiptOpen(true), []),
        closeReceipt: useCallback(() => setReceiptOpen(false), []),
    };
}
